import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { Col, Label, Row, Table, Container, CardBody, Card, Button, Input } from 'reactstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { ItemDescriptions, VariantSKU, SM, ML, WSEURO, QTY, ITEMTOTAL, Total } from '../../../../Constant';
import { H6, H4, P } from '../../../../AbstractElements';
import OrderHeader from './OrderHeader';
import OrderHeaderFields from './OrderHeaderFields';
import PrintInvoice from './PrintInvoice';
import CartContext from '../../../../_helper/Ecommerce/Cart';
import ProductContext from '../../../../_helper/Ecommerce/Product';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../../../../Config/api';

const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
const canEditOrderNumber = userRole === 'admin' || userRole === 'superadmin';

const ItemDescription = React.forwardRef((props, ref) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { symbol } = useContext(ProductContext);
  const isReadOnly = !!id;
  const [orderNumber, setOrderNumber] = useState(id || '');
  const [isEditingOrderNumber, setIsEditingOrderNumber] = useState(false);
  const [orderNumberInput, setOrderNumberInput] = useState('');
  const [orderNumberSaving, setOrderNumberSaving] = useState(false);
  const [orderNumberError, setOrderNumberError] = useState(null);
  const [formData, setFormData] = useState({
    store_name: '',
    shipping_address: '',
    billing_address: '',
    vat_eori: '',
    email: '',
    tel: '',
    order_date: new Date(),
    showroom: '',
    preferred_delivery_date: '',
    country: '',
  });

  const [productData, setProductData] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchOptions, setSearchOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!id) {
      axios.get(`${API_BASE_URL}/api/product-list`)
        .then(res => {
          const products = res.data.products.map(product => ({
            id: product.id,
            title: product.title,
            description: `${product.collection || ''} - ${product.colour} ${product.fabric}`.trim(),
            sku: product.sku,
            wholesale_price: parseFloat(product.wholesale_price || 0),
            s_qty: 0,
            m_qty: 0,
          }));
          setProductData(products);
          setItems(products);
        })
        .catch(err => {
          console.error('Error fetching products:', err);
        });
    }
  }, [id]);

  const handleQtyChange = (index, size, value) => {
    const newItems = [...items];
    newItems[index][size] = parseInt(value || 0);
    setItems(newItems);
  };

  const getRowTotalQty = (item) => item.s_qty + item.m_qty;
  const getRowTotalPrice = (item) => getRowTotalQty(item) * item.wholesale_price;
  const getOrderTotal = () => items.reduce((acc, item) => acc + getRowTotalPrice(item), 0);

  const handleProductSearch = useCallback((query) => {
    setIsSearching(true);
    axios.get(`${API_BASE_URL}/api/product-list`, { params: { search: query } })
      .then(res => {
        setSearchOptions(res.data.products || []);
      })
      .catch(err => {
        console.error('Product search failed:', err);
        setSearchOptions([]);
      })
      .finally(() => setIsSearching(false));
  }, []);

  const handleAddProduct = (selected) => {
    if (!selected || selected.length === 0) return;
    const product = selected[0];
    if (items.some(it => it.id === product.id)) {
      alert('This product is already in the order. Update its quantity in the row below.');
      return;
    }
    const wholesale = parseFloat(product.wholesale_price || 0);
    const retail = parseFloat(product.retail_euro || 0);
    setItems(prev => [
      ...prev,
      {
        id: product.id,
        title: product.title || '',
        sku: product.sku || '',
        collection: product.collection || '',
        colour: product.colour || '',
        fabric: product.fabric || '',
        wholesale_price: wholesale,
        retail_price: retail,
        margin: retail > 0 && wholesale > 0 ? retail - wholesale : 0,
        s_qty: 0,
        m_qty: 0,
      },
    ]);
  };

  const handleRemoveProduct = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/api/submit-order`, {
        order: formData,
        items: items.map(item => ({
          product_id: item.id,
          s_qty: item.s_qty,
          m_qty: item.m_qty,
          total_qty: getRowTotalQty(item),
          total_price: getRowTotalPrice(item),
          wholesale_price: item.wholesale_price || 0,
          retail_price: item.retail_price || 0,
          margin: item.margin || 0,
        })),
      });

      if (response.status === 200) {
        alert("Order submitted successfully!");
        setFormData({
          store_name: '',
          shipping_address: '',
          billing_address: '',
          vat_eori: '',
          email: '',
          tel: '',
          order_date: '',
          showroom: '',
          preferred_delivery_date: '',
        });
        setItems([]);
      } else {
        alert("Submission failed!");
      }
    } catch (error) {
      console.error(error);
      alert("Error submitting order");
    }
  };

  useEffect(() => {
    setOrderNumber(id || '');
  }, [id]);

  const handleStartEditOrderNumber = () => {
    setOrderNumberInput(orderNumber);
    setOrderNumberError(null);
    setIsEditingOrderNumber(true);
  };

  const handleCancelEditOrderNumber = () => {
    setIsEditingOrderNumber(false);
    setOrderNumberError(null);
  };

  const handleSaveOrderNumber = async () => {
    const newOrderId = orderNumberInput.trim().toUpperCase();
    if (!newOrderId) {
      setOrderNumberError('Order number is required');
      return;
    }
    if (newOrderId === orderNumber) {
      setIsEditingOrderNumber(false);
      return;
    }
    setOrderNumberSaving(true);
    setOrderNumberError(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/orders/${orderNumber}/order-number`,
        { newOrderId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setIsEditingOrderNumber(false);
      alert(
        `Order number updated to ${newOrderId}. Note: any documents already saved to Google Drive under ` +
        `the old order number are not renamed automatically.`
      );
      // The order id is part of the URL, so swap it in place rather than
      // refetching under the old id.
      navigate(location.pathname.replace(orderNumber, newOrderId), { replace: true });
    } catch (err) {
      setOrderNumberError(err.response?.data?.error || 'Failed to update order number');
    } finally {
      setOrderNumberSaving(false);
    }
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API_BASE_URL}/api/update-order/${id}`, {
        order: formData,
        items: items.map(item => ({
          product_id: item.id,
          s_qty: item.s_qty,
          m_qty: item.m_qty,
          total_qty: getRowTotalQty(item),
          total_price: getRowTotalPrice(item),
          wholesale_price: item.wholesale_price || 0,
          retail_price: item.retail_price || 0,
          margin: item.margin || 0,
        })),
      });

      if (response.status === 200) {
        alert("Order updated successfully!");
      } else {
        alert("Update failed!");
      }
    } catch (error) {
      console.error(error);
      alert("Error updating order");
    }
  };
  useEffect(() => {
    if (id) {
      console.log('Fetching order details for ID:', id);
      setLoading(true);
      setError(null);
      axios.get(`${API_BASE_URL}/api/order-detail/${id}`)
        .then(res => {
          console.log('API response:', res.data);
          const { order, items } = res.data;
  
          setFormData({
            store_name: order.store_name,
            shipping_address: order.shipping_address,
            billing_address: order.billing_address,
            vat_eori: order.vat_eori,
            email: order.email,
            tel: order.tel,
            order_date: new Date(order.order_date),
            showroom: order.showroom,
            preferred_delivery_date: order.preferred_delivery_date ? new Date(order.preferred_delivery_date) : '',
            country: order.country || '',
          });
  
          // Group items by product to combine sizes
          const groupedItems = {};
          items.forEach(item => {
            const key = `${item.product_id}-${item.title}-${item.sku}`;
            if (!groupedItems[key]) {
              groupedItems[key] = {
                id: item.product_id,
                title: item.title || 'Unknown Product',
                description: `${item.collection || 'Unknown Collection'} - ${item.colour || 'Unknown Color'} ${item.fabric || 'Unknown Fabric'}`.trim(),
                sku: item.sku,
                wholesale_price: parseFloat(item.wholesale_price || item.ws_euro || 0),
                retail_price: parseFloat(item.retail_euro || 0),
                margin: parseFloat(item.margin || 0),
                s_qty: 0,
                m_qty: 0,
                sizes: {}, // Store all sizes
                collection: item.collection || 'Unknown Collection',
                colour: item.colour || 'Unknown Color',
                fabric: item.fabric || 'Unknown Fabric'
              };
            }
            
            // Add quantities based on size
            if (item.size === 'SM') {
              groupedItems[key].s_qty += parseInt(item.quantity);
            } else if (item.size === 'ML') {
              groupedItems[key].m_qty += parseInt(item.quantity);
            }
            
            // Store all sizes for display
            groupedItems[key].sizes[item.size] = (groupedItems[key].sizes[item.size] || 0) + parseInt(item.quantity);
          });
          
          const processedItems = Object.values(groupedItems);
          console.log('Processed items:', processedItems);
          setItems(processedItems);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching order details:', err);
          setError('Failed to load order details');
          setLoading(false);
        });
    }
  }, [id]);
  console.log('ItemDescription rendering, id:', id, 'loading:', loading, 'error:', error, 'items.length:', items.length);
  
  return (
    <div ref={ref}>
      <style>
        {`
          .invoice-table {
            font-size: 12px;
          }
          .invoice-table th,
          .invoice-table td {
            padding: 8px 4px;
            vertical-align: middle;
            border: 1px solid #dee2e6;
          }
          .invoice-table th {
            background-color: #f8f9fa;
            font-weight: bold;
            text-align: center;
          }
          .invoice-table input[type="number"] {
            width: 60px;
            text-align: center;
            border: 1px solid #ced4da;
            padding: 4px;
          }
          .invoice-table .text-center {
            text-align: center;
          }
          .invoice-table .digits {
            font-family: 'Courier New', monospace;
          }
        `}
      </style>
      <Container className="invoice">
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <OrderHeader />
                {isReadOnly && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center">
                      <Label className="mb-0 me-2"><b>Order Number:</b></Label>
                      {isEditingOrderNumber ? (
                        <>
                          <Input
                            type="text"
                            value={orderNumberInput}
                            onChange={(e) => setOrderNumberInput(e.target.value)}
                            style={{ width: 180 }}
                            className="me-2"
                            autoFocus
                          />
                          <Button
                            color="success"
                            size="sm"
                            className="me-1"
                            type="button"
                            disabled={orderNumberSaving}
                            onClick={handleSaveOrderNumber}
                          >
                            {orderNumberSaving ? 'Saving...' : 'Save'}
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            type="button"
                            disabled={orderNumberSaving}
                            onClick={handleCancelEditOrderNumber}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="me-2">{orderNumber}</span>
                          {canEditOrderNumber && (
                            <Button color="link" size="sm" type="button" onClick={handleStartEditOrderNumber}>
                              Edit
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                    {orderNumberError && (
                      <div className="alert alert-danger py-1 px-2 mt-2 mb-0" role="alert" style={{ fontSize: 13 }}>
                        {orderNumberError}
                      </div>
                    )}
                  </div>
                )}
                {loading && (
                  <div className="text-center p-4">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Loading order details...</p>
                  </div>
                )}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                {!loading && !error && (
                  <form onSubmit={isReadOnly ? handleUpdateOrder : handleSubmit}>
                    <OrderHeaderFields formData={formData} setFormData={setFormData} isReadOnly={isReadOnly}/>
                    <Row className="my-3">
                      <Col md="8">
                        <Label className="mb-1"><b>Add product to order</b></Label>
                        <AsyncTypeahead
                          id="order-product-search"
                          isLoading={isSearching}
                          minLength={1}
                          delay={250}
                          onSearch={handleProductSearch}
                          options={searchOptions}
                          labelKey={(opt) =>
                            `${opt.title || ''} — ${opt.colour || 'No colour'}${opt.fabric ? ' / ' + opt.fabric : ''} (${opt.sku || ''})`
                          }
                          filterBy={() => true}
                          placeholder="Search by title, SKU, collection, colour, or fabric..."
                          onChange={(selected) => {
                            handleAddProduct(selected);
                            setSearchOptions([]);
                          }}
                          renderMenuItemChildren={(opt) => (
                            <div>
                              <div><b>{opt.title}</b> <span className="text-muted">({opt.sku})</span></div>
                              <small className="text-muted">
                                {opt.collection ? opt.collection + ' · ' : ''}
                                Colour: {opt.colour || '—'}
                                {opt.fabric ? ' · ' + opt.fabric : ''}
                                {opt.wholesale_price ? ` · ${symbol}${parseFloat(opt.wholesale_price).toFixed(2)}` : ''}
                              </small>
                            </div>
                          )}
                        />
                        <small className="text-muted">Pick a product to append a new row, then enter SM/ML quantities.</small>
                      </Col>
                    </Row>
                    {items.length === 0 ? (
                      <div className="text-center p-4">
                        <p>No items in this order yet. Use the search above to add products.</p>
                      </div>
                    ) : (
                    <div className="table-responsive invoice-table" id="table">
                    <Table bordered striped>
                      <thead>
                        <tr>
                          <td style={{ width: 40 }}></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>COLLECTION</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>TITLE</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>COLOUR</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>FABRIC</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>VARIANT SKU</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>SM</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>ML</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>WS EURO</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>RETAIL EURO</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>MARGIN</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>QTY</H4></td>
                          <td><H4 attrH4={{ className: 'p-2 mb-0' }}>ITEM TOTAL</H4></td>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          return (
                            <tr key={index}>
                              <td className="text-center">
                                <Button
                                  type="button"
                                  color="link"
                                  size="sm"
                                  onClick={() => handleRemoveProduct(index)}
                                  title="Remove product from order"
                                  style={{ color: '#dc3545', padding: 0, fontWeight: 'bold' }}
                                >
                                  ×
                                </Button>
                              </td>
                              <td>
                                <P attrPara={{ className: 'mb-0' }}>{item.collection}</P>
                              </td>
                              <td>
                                <Label className="mb-0">{item.title}</Label>
                              </td>
                              <td>
                                <P attrPara={{ className: 'mb-0' }}>{item.colour}</P>
                              </td>
                              <td>
                                <P attrPara={{ className: 'mb-0' }}>{item.fabric}</P>
                              </td>
                              <td>
                                <P attrPara={{ className: 'mb-0' }}>{item.sku}</P>
                              </td>
                              <td>
                                <Input
                                  type="hidden"
                                  name="product_id"
                                  value={item.id}
                                />
                                <Input
                                  type="number"
                                  min="0"
                                  name="s_qty"
                                  value={item.s_qty}
                                  onChange={(e) => handleQtyChange(index, 's_qty', e.target.value)}
                                  readOnly={false}
                                  className="text-center"
                                />
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  min="0"
                                  name="m_qty"
                                  value={item.m_qty}
                                  onChange={(e) => handleQtyChange(index, 'm_qty', e.target.value)}
                                  readOnly={false}
                                  className="text-center"
                                />
                              </td>
                              <td><P attrPara={{ className: 'itemtext digits text-center' }}>{symbol}{(item.wholesale_price || 0).toFixed(2)}</P></td>
                              <td><P attrPara={{ className: 'itemtext digits text-center' }}>{symbol}{(item.retail_price || 0).toFixed(2)}</P></td>
                              <td><P attrPara={{ className: 'itemtext digits text-center' }}>{(item.margin || 0).toFixed(2)}</P></td>
                              <td><P attrPara={{ className: 'itemtext digits text-center' }}>{getRowTotalQty(item)}</P></td>
                              <td><P attrPara={{ className: 'itemtext digits text-center' }}>{symbol}{getRowTotalPrice(item).toFixed(2)}</P></td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td colSpan="12" className="text-end">
                            <H6 attrH6={{ className: 'mb-0 p-2' }}>SUBTOTAL</H6>
                          </td>
                          <td>
                            <H6 attrH6={{ className: 'mb-0 p-2' }}>
                              {symbol}{getOrderTotal().toFixed(2)}
                            </H6>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="12" className="text-end">
                            <H6 attrH6={{ className: 'mb-0 p-2' }}>SHIPPING COST</H6>
                          </td>
                          <td>
                            <H6 attrH6={{ className: 'mb-0 p-2' }}>
                              {symbol}50.00
                            </H6>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="12" className="text-end">
                            <H6 attrH6={{ className: 'mb-0 p-2' }}>{Total}</H6>
                          </td>
                          <td>
                            <H6 attrH6={{ className: 'mb-0 p-2' }}>
                              {symbol}{(getOrderTotal() + 50).toFixed(2)}
                            </H6>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                    </div>
                    )}
                  <div className="d-flex justify-content-end">
                    <Button color="primary" type="submit">
                      {isReadOnly ? 'Update Order' : 'Submit Order'}
                    </Button>
                  </div>
                  </form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
});

export default ItemDescription;
